import { axiosInstance } from "../../lib/axios";
import { frontEndbaseUrl } from "../../config";
import {
  LoginRequest,
  LoginResponse,
  User,
  LeanLoginResponse,
  StaffLoginRequest,
  StaffLoginResponse,
  ModuleAccessCheckResponse,
  MyPlanModulesResponse,
  AvailablePlanModulesResponse,
} from "../../types/auth.types";

// ─────────────────────────────────────────────────────────────────────────────
// STANDARD LOGIN (Full response - ~150 lines)
// NOTE: Deprecated - Use leanLoginFn for better performance
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @deprecated Use leanLoginFn instead for 80% smaller payload
 */
export const loginFn = async ({
  email,
  password,
}: LoginRequest): Promise<LoginResponse> => {
  const response = await axiosInstance.post("api/Auth/login", {
    email,
    password,
  });
  return response.data;
};

// ─────────────────────────────────────────────────────────────────────────────
// LEAN LOGIN (Recommended - ~35 lines, 80% smaller)
// Use this for mobile-friendly, faster login
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lean login - recommended for production use
 * Returns ~80% smaller payload for faster mobile performance
 *
 * Response structure:
 * - access.modules: Plan modules (Layer 1)
 * - access.permissions: User permissions (Layer 2)
 *
 * Falls back to standard login if lean endpoint not available
 */
export const leanLoginFn = async ({
  email,
  password,
}: LoginRequest): Promise<LeanLoginResponse> => {
  try {
    // Try lean login first
    const response = await axiosInstance.post("api/Auth/login/lean", {
      email,
      password,
    });
    return response.data;
  } catch (error: any) {
    // If lean endpoint doesn't exist (404), fall back to standard login
    if (error?.response?.status === 404) {
      console.warn(
        "⚠️ Lean login endpoint not found, falling back to standard login",
      );
      const standardResponse = await axiosInstance.post("api/Auth/login", {
        email,
        password,
      });

      // Transform standard response to lean format
      const data = standardResponse.data;
      const user = data.user;

      return {
        success: data.success,
        message: data.message,
        token: data.token,
        user: {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          phoneNumber: user.phoneNumber,
          role: user.roles?.[0] || "user",
          roles: user.roles || [],
        },
        tenant: {
          id: user.tenantContext?.tenantId || 0,
          code: user.tenantContext?.tenantName || "",
          name: user.tenantContext?.tenantName || "",
          role: "TenantOwner",
          applicationId: 1,
          applicationCode: "RESTO",
          applicationName: "Restaurant Management",
        },
        restaurant: {
          id: user.restaurant?.id || 0,
          name: user.restaurant?.name || "",
          locationId: user.restaurant?.id || 0,
          locationName: user.restaurant?.name || "",
          isActive: user.restaurant?.isActive ?? true,
        },
        subscription: {
          planName: user.activeSubscription?.plan?.name || "Free Trial",
          isActive: user.activeSubscription?.isActive ?? false,
          daysRemaining: user.activeSubscription?.daysRemaining || 0,
          expiresAt: user.activeSubscription?.endDate || "",
          status: user.activeSubscription?.paymentStatus || "Unknown",
        },
        access: {
          // 🔥 Extract modules from tenantContext
          modules: user.tenantContext?.includedModules || [],
          // 🔥 Extract permissions from user.permissions
          permissions: user.permissions?.grantedPermissions || [],
          isOwner: user.permissions?.isOwner ?? false,
          isAdmin: user.permissions?.isAdmin ?? false,
          isSuperAdmin: user.permissions?.isSuperAdmin ?? false,
          hasAllLocations: true,
        },
      } as LeanLoginResponse;
    }
    throw error;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// STAFF LOGIN (PIN-based authentication)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Staff PIN login - for quick staff authentication
 * Uses employee ID + PIN instead of email/password
 */
export const staffLoginFn = async ({
  employeeId,
  pin,
}: StaffLoginRequest): Promise<StaffLoginResponse> => {
  const response = await axiosInstance.post("api/Auth/staff-login/lean", {
    employeeId,
    pin,
  });
  return response.data;
};

export const registerFn = async (payload: {
  firstName: string;
  lastName: string;
  email: string;
  userName: string;
  password: string;
  phoneNumber: string;
  role: string;
  name?: string;
  business_name?: string;
  business_type?: string;
  phone?: string;
  address?: string;
}) => {
  const response = await axiosInstance.post("api/Auth/register", payload);

  return response.data;
};

/**
 * Fetch current user profile with permissions
 * This endpoint returns fresh user data including availableFeatures array
 * Called on app initialization to ensure features are always up-to-date
 *
 * Returns the full ProfileResponse with user, tenant, restaurant, subscription, and access data
 */
export const getUserProfileFn = async (): Promise<any> => {
  // Add timestamp to prevent caching
  const response = await axiosInstance.get(
    `api/Auth/profile?_t=${Date.now()}`,
    {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    },
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────────────────
// MODULE ACCESS CHECK API
// Endpoints for checking module access at runtime
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get current user's plan modules with access status
 * GET /api/StaffManagement/my-plan-modules
 */
export const getMyPlanModulesFn = async (): Promise<MyPlanModulesResponse> => {
  const response = await axiosInstance.get(
    "api/StaffManagement/my-plan-modules",
  );
  return response.data;
};

/**
 * Check if a specific module is included in the current plan
 * GET /api/StaffManagement/check-module-access/{moduleKey}
 *
 * @param moduleKey - The module key to check (e.g., 'INVENTORY', 'HR')
 */
export const checkModuleAccessFn = async (
  moduleKey: string,
): Promise<ModuleAccessCheckResponse> => {
  const response = await axiosInstance.get(
    `api/StaffManagement/check-module-access/${moduleKey}`,
  );
  return response.data;
};

/**
 * Get all available modules (master list for upgrade screens)
 * GET /api/StaffManagement/available-plan-modules
 */
export const getAvailablePlanModulesFn =
  async (): Promise<AvailablePlanModulesResponse> => {
    const response = await axiosInstance.get(
      "api/StaffManagement/available-plan-modules",
    );
    return response.data;
  };

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE MANAGEMENT
// Endpoints for updating user profile and password
// ─────────────────────────────────────────────────────────────────────────────

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
}

/**
 * Update user profile information (name, email, phone)
 * PUT /api/Auth/profile
 */
export const updateProfileFn = async (
  data: UpdateProfileRequest,
): Promise<AuthResponse> => {
  const response = await axiosInstance.put("api/Auth/profile", data);
  return response.data;
};

/**
 * Change user password (requires old password)
 * POST /api/Auth/change-password
 */
export const changePasswordFn = async (
  data: ChangePasswordRequest,
): Promise<AuthResponse> => {
  const response = await axiosInstance.post("api/Auth/change-password", data);
  return response.data;
};

/**
 * Request a password reset link via email
 * POST /api/Auth/forgot-password
 */
export const forgotPasswordFn = async (
  email: string,
): Promise<AuthResponse> => {
  const response = await axiosInstance.post("api/Auth/forgot-password", {
    email,
    clientUri: frontEndbaseUrl
  });
  return response.data;
};
